import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from '@/features/snackbar';

import {
  mutationFn,
  type NarrativeAnnouncementCreationResponseData,
} from './mutationFn';

import { getQueryKey } from '../useNarrative';
import type { Narrative } from '../useNarrative/types';

type Options = {
  onSuccess?: () => void;
  slug: string;
};

export const useCreateNarrativeAnnouncement = (
  options: Options = { slug: '' }
) => {
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: mutationFn,
    onSuccess: (data: NarrativeAnnouncementCreationResponseData) => {
      const NARRATIVE_QUERY_KEY = getQueryKey(options.slug);
      queryClient.setQueryData<Narrative>(NARRATIVE_QUERY_KEY, (state) => {
        if (typeof state === 'undefined') return state;
        const announcements = state.announcements || [];
        return {
          ...state,
          announcements: [...announcements, data],
        };
      });
      addSnackbar({
        type: 'positive',
        lifeTime: 1000,
        message: 'Narrative announcement successfully created',
      });
      options.onSuccess?.();
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        lifeTime: 2000,
        message: 'Failed to create narrative',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getQueryKey(options.slug) });
    },
  });
};
